import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CaretRight as ChevronRight,
  House as Home
} from '@phosphor-icons/react';

// Route name mappings
const routeNames = {
  '': 'Dashboard',
  'agents': 'Agentes',
  'agent-health': 'Salud de Agentes',
  'network': 'Mapa de Red',
  'network-scanner': 'Scanner de Red',
  'vulnerabilities': 'Vulnerabilidades',
  'dlp': 'DLP',
  'ad-attacks': 'Ataques AD',
  'alerts': 'Alertas',
  'remediation': 'Remediación',
  'data-leaks': 'Fugas de Datos',
  'metrics': 'Métricas',
  'reports': 'Reportes',
  'siem-integrations': 'SIEM',
  'users': 'Usuarios',
  'settings': 'Configuración',
  'download': 'Descargar',
};

// Route parent mappings for hierarchy
const routeParents = {
  'agent-health': 'agents',
  'network-scanner': 'network',
  'dlp': 'vulnerabilities',
  'ad-attacks': 'vulnerabilities',
  // Retirada T5 (2026-10-13): el padre era 'incident-response', pantalla retirada.
  // El nuevo padre de la rama de respuesta es la bandeja de alertas.
  'remediation': 'alerts',
  'data-leaks': 'alerts',
  'reports': 'metrics',
  'siem-integrations': 'metrics',
};

const Breadcrumbs = ({ items, className = '' }) => {
  const location = useLocation();

  // Auto-generate breadcrumbs from location if items not provided
  const generateBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);

    if (pathParts.length === 0) {
      return [{ name: 'Dashboard', path: '/', current: true }];
    }

    const breadcrumbs = [{ name: 'Dashboard', path: '/' }];

    // Check if current route has a parent
    const currentRoute = pathParts[0];
    const parent = routeParents[currentRoute];

    if (parent) {
      breadcrumbs.push({
        name: routeNames[parent] || parent,
        path: `/${parent}`,
      });
    }

    // Add current route
    let currentPath = '';
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      const isLast = index === pathParts.length - 1;

      // Skip if it's a dynamic segment (like agent ID)
      const isDynamic = /^[0-9a-f-]{36}$/.test(part);

      if (!isDynamic || isLast) {
        breadcrumbs.push({
          name: isDynamic ? 'Detalles' : (routeNames[part] || part),
          path: currentPath,
          current: isLast,
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();

  if (breadcrumbItems.length <= 1) {
    return null; // Don't show breadcrumbs on home page
  }

  return (
    <nav className={`flex items-center text-[13px] ${className}`} aria-label="Breadcrumb">
      <ol className="flex items-center gap-1">
        {breadcrumbItems.map((item, index) => (
          <li key={item.path} className="flex items-center">
            {index > 0 && (
              <ChevronRight
                className="h-3.5 w-3.5 text-slate-600 mx-1.5"
                weight="regular"
                aria-hidden="true"
              />
            )}

            {item.current ? (
              <span className="text-slate-100 font-medium tracking-tight flex items-center">
                {index === 0 && <Home className="h-3.5 w-3.5 inline mr-1.5 text-cyan-400/80" weight="regular" aria-hidden="true" />}
                {item.name}
              </span>
            ) : (
              <Link
                to={item.path}
                className="text-slate-400 hover:text-cyan-400 transition-colors flex items-center"
              >
                {index === 0 && <Home className="h-3.5 w-3.5 mr-1.5" weight="regular" aria-hidden="true" />}
                {index === 0 ? '' : item.name}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
